import type { IdName, Paginate } from '../common';
import type { IWorkerType } from '../workers/types';

export const UserProfiles = {
  agilizador: 1,
  desarrollador: 2,
  administrador: 3,
  trabajador: 4,
  cliente: 5,
  administrador_empresa: 6,
  cliente_empresa: 7,
  coordinador: 8,
  comercial: 9,
  soporte_plataforma: 11,
  producto: 12,
  controller_go: 13,
  onboarding: 14,
  cargador_masivo: 43,
  tarifador: 77,
  controller_carga: 78,
  aprobador: 175,
  cumplidor: 176,
  facturador: 208,
  provider: 241,
} as const;

/**
 * @url /users/config
 * @method GET
 * @type Response
 * @params pagination=true
 */
export type UsersPaginated = Paginate<IConfigUser>;

/**
 * @url /users/config
 * @method GET
 * @type Response
 * @params pagination=false
 */
export interface IConfigUser {
  id: number;
  first_name: string;
  last_name: string;
  birth_date: string | null;
  active?: boolean;
  profile_image: string | null;
  email: string;
  mobile_phone: string;
  nid: string;
  gender: 'M' | 'F' | 'NB' | null;
  nids: IdName | null;
  city: IdName;
  country: IdName;
  address: string;
  profiles: IdName[];
  countries: IdName[];
  lines: { id: number; name: string }[];
  money_profile: IdName;
  company: Company | null;
  worker: Worker | null;
  vehicle: Vehicle | null;
  two_factor: boolean;
  status: string;
}

export interface Company {
  id?: number;
  company_name?: string;
  configurations?: {
    workers?: {
      services?: {
        limit_quantity?: number;
      };
    };
  };
  legal_name?: string;
  nid_company?: string;
  branch_office_id?: number;
  contact_role?: string;
  billing_address?: string;
  nids?: string;
  profit_percentage?: number;
}
interface Vehicle {
  id: string;
  car_plate: string;
  vehicle_type: IdName;
  bici_id: number | null;
  model: string;
  number_property_card?: number | null;
  vehicle_class?: {
    id: number;
    name: string;
  };
  brand?: {
    id: number;
    name: string;
  };
}

interface Worker {
  id: number | null;
  license?: string;
  category_license?: IdName;
  mobile_phone_contact?: string;
  worker_type: IWorkerType;
}

/**
 * @url /users/config
 * @method POST
 * @description Response for create a new user, and optionally a new worker profile with vehicle, and associate it to a company
 * @type Response
 */
export interface INewUserRes {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  mobile_phone: string;
  nid: string;
  nids_id: number;
  nids_id_name: string;
  max_declared_value: number;
  address: string;
  city_id: number;
  city_name: string;
  country_id: number;
  country_name: string;
  money_profile_id: IdName;
  profiles: IdName[];
  company: null;
  company_branch: null;
  worker?: {
    id: number;
    user_id: number;
    mobile_phone_contact: string;
    license: string;
    category_license: IdName;
    license_expiration: null;
    issue_date_license: null;
    emergency_mobile_phone: null;
    emergency_contact_full_name: null;
    emergency_contact_nid: null;
    emergency_contact_address: null;
    emergency_contact_mobile_phone: null;
    hired: null;
  };
  vehicle?: {
    id: number;
    model: string;
    insurance_number: null;
    secure_expiration: null;
    number_technomechanical: null;
    technomechanical_date_expiration: null;
    number_property_card: number;
    issuance_date_property_card: null;
    vehicle_type: IdName;
    insurance_company: null;
    brand: IdName;
    vehicle_class: IdName;
    worker_id: number;
    bici_id: null;
  };
  /** Single Use Token. Only present when the user password is not set. Base64 encoded */
  sut?: string;
  two_factor: boolean;
}

/**
 * @url /users/config
 * @method POST
 * @description Body for create a new user, and optionally a new worker profile with vehicle, and associate it to a company
 * @type Request
 */
export interface ConfigUserBodyPost {
  first_name: string;
  last_name: string;
  birth_date?: string;
  nid: string;
  nids_id: string;
  mobile_phone: string;
  email: string;
  password: string;
  password_confirmation: string;
  city_id: string;
  address: string;
  money_profile_id: string;
  profiles: number[];
  countries: number[];
  lines: number[];
  gender: string;
  worker?: {
    license: string;
    category_license_id: string;
    license_expiration: null;
    issue_date_license: null;
    emergency_mobile_phone: null;
    emergency_contact_full_name: null;
    emergency_contact_nid: null;
    emergency_contact_address: null;
    emergency_contact_mobile_phone: null;
    hired: null;
    worker_type: string;
    provider: string;
  };
  vehicle?: {
    model: string;
    car_plate: string;
    vehicle_type_id: string;
    brand_id: string | null;
    insurance_number: null;
    secure_expiration: null;
    number_technomechanical: null;
    technomechanical_date_expiration: null;
    number_property_card: number | string | null;
    issuance_date_property_card: null;
    insurance_company_id: null;
    bici_id: null;
    vehicle_class_id: string | null;
  };
  company?: Company | null;
  branch_office_id?: string;
}
